import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef } from "react";

type SessionSchedule = {
  scheduledDate: string;
  scheduledTime: string | null;
};

export function UndoCompletionDialog({
  activityTitle,
  session,
  pending,
  error,
  onConfirm,
  onDismiss,
}: {
  activityTitle: string;
  session: SessionSchedule;
  pending: boolean;
  error: string;
  onConfirm: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    keepButtonRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="undo-completion-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onDismiss();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget || pending) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const outsideDialog =
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom;
        if (outsideDialog) onDismiss();
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <span className="section-kicker">Undo completion</span>
      <h2 id={headingId}>Undo this completion?</h2>
      <div className="undo-completion-summary">
        <strong>{activityTitle}</strong>
        <span>{formatSessionSchedule(session)}</span>
      </div>
      <p id={descriptionId}>
        This session will return to scheduled and will no longer count as completed.
      </p>
      {error ? (
        <p className="undo-completion-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="undo-completion-actions">
        <button
          ref={keepButtonRef}
          type="button"
          className="soft-button"
          disabled={pending}
          onClick={onDismiss}
        >
          Keep completed
        </button>
        <button type="button" className="danger-button" disabled={pending} onClick={onConfirm}>
          {pending ? "Undoing…" : "Undo completion"}
        </button>
      </div>
    </dialog>
  );
}

function formatSessionSchedule(session: SessionSchedule): string {
  const [year, month, day] = session.scheduledDate.split("-").map(Number);
  const date =
    year && month && day
      ? new Intl.DateTimeFormat(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(year, month - 1, day)))
      : session.scheduledDate;
  if (!session.scheduledTime) return `${date} · Untimed`;
  const [hour, minute] = session.scheduledTime.split(":").map(Number);
  const time =
    Number.isInteger(hour) && Number.isInteger(minute)
      ? new Intl.DateTimeFormat(undefined, {
          hour: "numeric",
          minute: "2-digit",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(2000, 0, 1, hour, minute)))
      : session.scheduledTime;
  return `${date} · ${time}`;
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
